import { ApiResponse } from 'apisauce';
import { SagaIterator } from 'redux-saga';
import {
  all,
  call,
  put,
  select,
  takeLatest,
  takeLeading,
} from 'redux-saga/effects';
import API from '../api';
import {
  BookingModuleSelectors,
  finishOwnerBookingModulesSave,
  getOwnerBookingModules,
  replaceOwnerBookingModule,
  saveOwnerBookingModules,
  setOwnerBookingModuleSaveError,
  setOwnerBookingModuleSlugError,
  setOwnerBookingModules,
  setOwnerBookingModulesError,
} from '../reducers/bookingModuleSlice';
import { setMessage } from '../reducers/pageSlice';
import {
  BookingModuleSaveError,
  OwnerBookingModule,
  OwnerBookingModuleChange,
} from '../types/bookingModuleTypes';
import { MessageTypes } from '../../utils/@globalTypes';
import callCheckingAuth from './callCheckingAuth';

type BookingModuleSaveResult =
  | { saved: true }
  | { saved: false; error: BookingModuleSaveError; code: number };

const saveErrorMessages: Record<BookingModuleSaveError, string> = {
  free_tariff: 'Для работы с Модулем бронирования нужен платный тариф.',
  not_found: 'Модуль не найден. Обновите страницу и попробуйте снова.',
  request_failed: 'Не удалось сохранить настройки модулей',
};

const getSlugValidationError = (data: unknown): string | null => {
  if (!data || typeof data !== 'object') return null;

  const slugError = (data as Record<string, unknown>).slug;
  if (typeof slugError === 'string') return slugError;

  if (Array.isArray(slugError)) {
    return (
      slugError.find((error): error is string => typeof error === 'string') ||
      null
    );
  }

  return null;
};

export function* getOwnerBookingModulesWorker(): SagaIterator<void> {
  const isSaving: boolean = yield select(BookingModuleSelectors.getIsSaving);
  if (isSaving) return;
  const response: ApiResponse<OwnerBookingModule[]> = yield call(
    callCheckingAuth,
    API.getOwnerBookingModulesRequest,
  );

  if (response.ok && response.data) {
    yield put(setOwnerBookingModules(response.data));
    return;
  }

  yield put(
    setOwnerBookingModulesError(
      response.status === 403 ? 'free_tariff' : 'request_failed',
    ),
  );
}

function* saveOwnerBookingModule(
  change: OwnerBookingModuleChange,
): SagaIterator<BookingModuleSaveResult> {
  let response: ApiResponse<OwnerBookingModule>;
  try {
    response = yield call(
      callCheckingAuth,
      API.updateOwnerBookingModuleRequest,
      change.id,
      change.data,
    );
  } catch {
    yield put(
      setOwnerBookingModuleSaveError({
        id: change.id,
        error: 'request_failed',
      }),
    );
    return { saved: false, error: 'request_failed', code: 500 };
  }

  if (response.ok && response.data) {
    yield put(replaceOwnerBookingModule(response.data));
    return { saved: true };
  }

  const slugError =
    response.status === 400 ? getSlugValidationError(response.data) : null;

  if (slugError) {
    yield put(
      setOwnerBookingModuleSlugError({ id: change.id, error: slugError }),
    );
    return {
      saved: false,
      error: 'request_failed',
      code: response.status || 400,
    };
  }

  const error: BookingModuleSaveError =
    response.status === 403
      ? 'free_tariff'
      : response.status === 404
        ? 'not_found'
        : 'request_failed';
  yield put(setOwnerBookingModuleSaveError({ id: change.id, error }));
  return { saved: false, error, code: response.status || 500 };
}

export function* saveOwnerBookingModulesWorker(): SagaIterator<void> {
  const isSaving: boolean = yield select(BookingModuleSelectors.getIsSaving);
  if (!isSaving) return;
  const changes: OwnerBookingModuleChange[] = yield select(
    BookingModuleSelectors.getChanges,
  );
  let savedCount = 0;
  let firstFailure: Extract<BookingModuleSaveResult, { saved: false }> | null =
    null;

  try {
    for (const change of changes) {
      const result: BookingModuleSaveResult = yield call(
        saveOwnerBookingModule,
        change,
      );
      if (result.saved) {
        savedCount += 1;
      } else if (!firstFailure) {
        firstFailure = result;
      }
    }

    yield put(
      setMessage({
        status: firstFailure ? MessageTypes.ERROR : MessageTypes.POSITIVE,
        code: firstFailure?.code || 200,
        message: firstFailure
          ? savedCount > 0
            ? 'Часть настроек сохранена. Не удалось сохранить остальные изменения.'
            : saveErrorMessages[firstFailure.error]
          : 'Настройки модулей сохранены',
      }),
    );
  } finally {
    yield put(finishOwnerBookingModulesSave());
  }
}

export default function* bookingModuleSaga(): SagaIterator<void> {
  yield all([
    takeLatest(getOwnerBookingModules.type, getOwnerBookingModulesWorker),
    takeLeading(saveOwnerBookingModules.type, saveOwnerBookingModulesWorker),
  ]);
}
